import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { useConverti } from '../api/scheda.js';
import { Modale } from '../componenti/Modale.js';
import { etichettaTipoAnagrafica } from '../etichette.js';
import { type Sezione, usePuoAprire } from '../permessi.js';
import { t } from '../i18n/lingua.js';

/**
 * Convertire un'anagrafica: lead → prospect → cliente.
 *
 * Nel v2 la finestra c'è e **si apre vuota**: carica i tipi filtrando su
 * `showInConvertSection`, che nei dati è falso su tutte e cinque le righe.
 * Qui i tipi sono quelli veri, meno uno.
 *
 * **L'operatore non c'è, né come partenza né come arrivo.** Ha le credenziali
 * per entrare nel programma: farlo diventare cliente gliele lascerebbe
 * attaccate, e fare diventare operatore un cliente sarebbe dare l'accesso da
 * una finestra che di accessi non parla. Il servizio lo rifiuta comunque.
 *
 * Dopo la conversione si va **sull'elenco nuovo**: restare sull'indirizzo
 * vecchio — `/leads/482` per una riga che adesso è un cliente — funziona per
 * caso, e il collegamento «torna all'elenco» riporterebbe dove la riga non
 * c'è più.
 */

/** Dove vive ogni tipo. Sono gli stessi indirizzi del menu. */
const ELENCHI: Record<string, string> = {
  customer: '/customers',
  supplier: '/suppliers',
  agent: '/agents',
  lead: '/leads',
  prospect: '/prospects',
};

const TIPI = ['lead', 'prospect', 'customer', 'agent', 'supplier'] as const;

export function ModaleConverti({
  id,
  nome,
  tipo,
  onChiudi,
}: {
  id: number;
  nome: string;
  tipo: string;
  onChiudi: () => void;
}) {
  const [scelto, setScelto] = useState<string | null>(null);
  const converti = useConverti(id);
  const naviga = useNavigate();

  const errore = converti.error instanceof ErroreApi ? converti.error : null;
  // solo i tipi che chi converte può aprire: al commerciale si offriva
  // «Fornitore», e poi il servizio glielo rifiutava
  const puoAprire = usePuoAprire();
  const possibili = TIPI.filter((v) => v !== tipo && puoAprire(ELENCHI[v]!.slice(1) as Sezione));

  const applica = () => {
    if (!scelto) return;
    converti.mutate(scelto, {
      onSuccess: () => {
        onChiudi();
        void naviga({ to: `${ELENCHI[scelto]}/${id}`, replace: true });
      },
    });
  };

  return (
    <Modale
      titolo={t('Converti l’anagrafica')}
      occhiello={nome}
      nota={t('Cambia il tipo, e con quello l’elenco in cui si trova. Tutto il resto — indirizzi, contatti, ordini, storico — resta attaccato dov’è.')}
      inCorso={converti.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={converti.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            disabled={!scelto || converti.isPending}
            onClick={applica}
          >
            {converti.isPending ? t('Converto…') : t('Converti')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      <div className="field">
        <label>
          {t('Adesso è')} <strong>{etichettaTipoAnagrafica(tipo)}</strong>. {t('Diventa:')}
        </label>
        <div className="scelte">
          {possibili.map((v) => (
            <button
              key={v}
              className="preset"
              aria-pressed={scelto === v}
              onClick={() => setScelto(v)}
            >
              {etichettaTipoAnagrafica(v)}
            </button>
          ))}
        </div>
      </div>
    </Modale>
  );
}
